import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { BrowserProvider } from 'ethers';
import { getNonce, verifySignature, revokeSession, getEntitlements, restoreSubscription } from '../services/api.js';
import { trackConversion } from '../services/conversionTracking.js';

interface AuthState {
  token: string | null;
  walletAddress: string | null;
  isAuthenticated: boolean;
  isConnecting: boolean;
  authLoading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  connectWallet: () => Promise<void>;
  disconnect: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = 'snap_auth_token';
const WALLET_KEY = 'snap_wallet_address';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const walletAddress = localStorage.getItem(WALLET_KEY);
    return {
      token,
      walletAddress,
      isAuthenticated: !!token,
      isConnecting: false,
      authLoading: true, // always true until mount validation completes
      error: null,
    };
  });

  // Validate persisted token on mount
  useEffect(() => {
    if (!state.token) {
      setState((s) => ({ ...s, authLoading: false }));
      return;
    }

    // Simple JWT expiry check (decode payload without verification)
    try {
      const payload = JSON.parse(atob(state.token.split('.')[1]));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(WALLET_KEY);
        setState({
          token: null,
          walletAddress: null,
          isAuthenticated: false,
          isConnecting: false,
          authLoading: false,
          error: null,
        });
      } else {
        setState((s) => ({ ...s, authLoading: false }));
      }
    } catch {
      // Malformed token — clear it
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(WALLET_KEY);
      setState({
        token: null,
        walletAddress: null,
        isAuthenticated: false,
        isConnecting: false,
        authLoading: false,
        error: null,
      });
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectWallet = useCallback(async () => {
    setState((s) => ({ ...s, isConnecting: true, error: null }));

    try {
      // Check for MetaMask
      if (!window.ethereum) {
        throw new Error('MetaMask is not installed. Please install MetaMask to continue.');
      }

      // Step 1 — Request MetaMask access
      const provider = new BrowserProvider(window.ethereum);
      await provider.send('eth_requestAccounts', []);

      // Step 2 — Get signer and address
      const signer = await provider.getSigner();
      const address = await signer.getAddress();

      if (!address) {
        throw new Error('Wallet connection failed');
      }

      // Step 3 — Get nonce from API
      const { nonce } = await getNonce();

      // Construct EIP-4361 SIWE message
      const domain = window.location.host;
      const origin = window.location.origin;
      const issuedAt = new Date().toISOString();

      const message = [
        `${domain} wants you to sign in with your Ethereum account:`,
        address,
        '',
        'Sign in to CryptoGuardians',
        '',
        `URI: ${origin}`,
        'Version: 1',
        `Chain ID: 1`,
        `Nonce: ${nonce}`,
        `Issued At: ${issuedAt}`,
      ].join('\n');

      // Sign message
      const signature = await signer.signMessage(message);

      // Verify with API
      const result = await verifySignature(address, message, signature);

      // Persist
      localStorage.setItem(TOKEN_KEY, result.token);
      localStorage.setItem(WALLET_KEY, result.walletAddress);

      setState({
        token: result.token,
        walletAddress: result.walletAddress,
        isAuthenticated: true,
        isConnecting: false,
        authLoading: false,
        error: null,
      });

      trackConversion('wallet_connected');

      // Redirect to Account Center after successful authentication
      window.location.href = '/wallet';

      // Non-blocking: auto-restore subscription if wallet is on free tier
      getEntitlements(result.token)
        .then((ent) => {
          if (ent.tier === 'free') {
            restoreSubscription(result.token).catch(() => {});
          }
        })
        .catch(() => {});
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to connect wallet';
      setState((s) => ({
        ...s,
        isConnecting: false,
        error: message,
      }));
    }
  }, []);

  const disconnect = useCallback(async () => {
    try {
      if (state.token) {
        await revokeSession(state.token).catch(() => {
          // Best effort — clear locally even if API call fails
        });
      }
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(WALLET_KEY);
      setState({
        token: null,
        walletAddress: null,
        isAuthenticated: false,
        isConnecting: false,
        authLoading: false,
        error: null,
      });
    }
  }, [state.token]);

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        connectWallet,
        disconnect,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
